import type { AreaData, AreaDataProperty } from "@workadventure/map-editor";
import type { Unsubscriber } from "svelte/store";
import type { SpaceUserExtended } from "../Space/SpaceInterface";
import { gameManager } from "../Phaser/Game/GameManager";
import type { RemotePlayer } from "../Phaser/Entity/RemotePlayer";
import { audioPlaybackStore, signalAudioPlaybackBlocked } from "../Stores/AudioPlaybackStore";
import { userActivationManager } from "../Stores/UserActivationStore";
import { audioContextManager } from "./AudioContextManager";
import {
    clamp,
    computePointMix,
    quietFallbackMix,
    resolveSpatialMix,
    shouldWaitForUserActivation,
    type ResolvedSpatialMix,
    type SpatialMix,
    type SpatialPoint,
} from "./spatial-audio-core";
import {
    ENABLE_SPATIAL_AUDIO,
    SPATIAL_AUDIO_FAR_RADIUS,
    SPATIAL_AUDIO_MIN_GAIN,
    SPATIAL_AUDIO_NEAR_RADIUS,
} from "../Enum/EnvironmentVariable";

const UPDATE_INTERVAL_MS = 100;
export const SPATIAL_AUDIO_EXTENSION_SUBTYPE = "dystopiaSpatialAudio";

interface PointShape {
    mode: "point";
    x: number;
    y: number;
}

interface ZoneShape {
    mode: "zone";
    x: number;
    y: number;
    width: number;
    height: number;
}

type SpatialShape = PointShape | ZoneShape;

interface WorldSpatialAudioConfig {
    id: string;
    url: string;
    volume: number;
    loop: boolean;
    nearRadius: number;
    farRadius: number;
    minGain: number;
    shape: SpatialShape;
}

interface WorldSpatialAudioSource extends WorldSpatialAudioConfig {
    audioElement: HTMLAudioElement;
    sourceNode: MediaElementAudioSourceNode;
    gainNode: GainNode;
    pannerNode: StereoPannerNode;
    blocked: boolean;
}

type SpatialAudioExtensionProperty = Extract<AreaDataProperty, { type: "extensionModule" }> & {
    data?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function optionalNumber(value: unknown, fallback: number): number {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function optionalBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === "boolean" ? value : fallback;
}

function extractUserId(spaceUserId: string): number | undefined {
    const match = /(?:^|_)(-?\d+)$/.exec(spaceUserId);
    if (!match) return undefined;
    const userId = Number(match[1]);
    return Number.isFinite(userId) ? userId : undefined;
}

function findRemotePlayer(spaceUser: SpaceUserExtended): RemotePlayer | undefined {
    const scene = gameManager.getCurrentGameScene();
    for (const remotePlayer of scene.MapPlayersByKey.values()) {
        if (remotePlayer.userUuid === spaceUser.uuid) {
            return remotePlayer;
        }
    }

    const userId = extractUserId(spaceUser.spaceUserId);
    return userId === undefined ? undefined : scene.MapPlayersByKey.get(userId);
}

function getCameraRotation(): number {
    try {
        return gameManager.getCurrentGameScene().cameras.main.rotation ?? 0;
    } catch {
        return 0;
    }
}

function computeMixFromPoint(
    listener: { x: number; y: number },
    x: number,
    y: number,
    baseVolume: number,
    nearRadius?: number,
    farRadius?: number,
    minGain?: number,
): SpatialMix {
    return computePointMix(
        listener,
        { x, y },
        {
            baseVolume,
            nearRadius: nearRadius ?? SPATIAL_AUDIO_NEAR_RADIUS,
            farRadius: farRadius ?? SPATIAL_AUDIO_FAR_RADIUS,
            minGain: minGain ?? SPATIAL_AUDIO_MIN_GAIN,
            cameraRotation: getCameraRotation(),
        },
    );
}

function closestPointOnRect(listenerX: number, listenerY: number, shape: ZoneShape): { x: number; y: number; distance: number } {
    const maxX = shape.x + shape.width;
    const maxY = shape.y + shape.height;
    const x = clamp(listenerX, shape.x, maxX);
    const y = clamp(listenerY, shape.y, maxY);
    const inside = listenerX >= shape.x && listenerX <= maxX && listenerY >= shape.y && listenerY <= maxY;
    return {
        x,
        y,
        distance: inside ? 0 : Math.hypot(listenerX - x, listenerY - y),
    };
}

function resolveAudioUrl(audioLink: string, mapUrl: string): string {
    if (/^[a-z][a-z0-9+.-]*:/i.test(audioLink)) {
        return audioLink;
    }
    if (audioLink.startsWith("/")) {
        return new URL(audioLink, window.location.origin).toString();
    }
    return new URL(audioLink, mapUrl).toString();
}

function computeSpatialMix(
    spaceUser: SpaceUserExtended,
    baseVolume: number,
    lastSourcePosition?: SpatialPoint,
): ResolvedSpatialMix {
    try {
        const scene = gameManager.getCurrentGameScene();
        const listener = scene.CurrentPlayer;
        const source = findRemotePlayer(spaceUser);
        return resolveSpatialMix({
            listener: listener ? { x: listener.x, y: listener.y } : undefined,
            observedSource: source ? { x: source.x, y: source.y } : undefined,
            lastSourcePosition,
            baseVolume,
            nearRadius: SPATIAL_AUDIO_NEAR_RADIUS,
            farRadius: SPATIAL_AUDIO_FAR_RADIUS,
            minGain: SPATIAL_AUDIO_MIN_GAIN,
            cameraRotation: getCameraRotation(),
        });
    } catch {
        return {
            mix: quietFallbackMix(baseVolume, SPATIAL_AUDIO_MIN_GAIN),
            sourcePosition: lastSourcePosition,
        };
    }
}

export function createSpatialAudioController() {
    let audioContext: AudioContext | undefined;
    let sourceNode: MediaStreamAudioSourceNode | undefined;
    let gainNode: GainNode | undefined;
    let pannerNode: StereoPannerNode | undefined;
    let interval: ReturnType<typeof setInterval> | undefined;
    let currentStream: MediaStream | undefined;
    let currentSpaceUser: SpaceUserExtended | undefined;
    let currentBaseVolume = 1;
    let lastSourcePosition: SpatialPoint | undefined;
    let resumeRetryUnregister: Unsubscriber | undefined;
    let activationWaitScheduled = false;
    let resumeInFlight = false;

    function clearResumeRetry(): void {
        resumeRetryUnregister?.();
        resumeRetryUnregister = undefined;
    }

    async function attemptResume(): Promise<void> {
        const context = audioContext;
        if (!currentStream || !context) {
            clearResumeRetry();
            return;
        }
        if (context.state !== "suspended") {
            if (context.state === "running") clearResumeRetry();
            return;
        }
        if (resumeInFlight) return;

        resumeInFlight = true;
        try {
            await context.resume();
        } catch (error) {
            if (!(error instanceof DOMException) || error.name !== "NotAllowedError") {
                console.warn("Spatial audio context could not resume.", error);
            }
        } finally {
            resumeInFlight = false;
        }

        if (context.state === "running") {
            clearResumeRetry();
        } else {
            scheduleResumeRetry();
        }
    }

    function scheduleResumeRetry(): void {
        if (!currentStream || !audioContext || audioContext.state !== "suspended") return;

        if (!resumeRetryUnregister) {
            signalAudioPlaybackBlocked();
            resumeRetryUnregister = audioPlaybackStore.register(attemptResume);
        }
        if (!activationWaitScheduled) {
            activationWaitScheduled = true;
            void userActivationManager
                .waitForUserActivation()
                .then(attemptResume)
                .catch((error: unknown) => console.warn("Spatial audio activation retry failed.", error))
                .finally(() => {
                    activationWaitScheduled = false;
                });
        }
    }

    function ensureAudioContextRunning(): void {
        if (!audioContext || audioContext.state !== "suspended") {
            if (audioContext?.state === "running") clearResumeRetry();
            return;
        }

        const hasBeenActive = "userActivation" in navigator ? navigator.userActivation.hasBeenActive : undefined;
        if (shouldWaitForUserActivation(audioContext.state, hasBeenActive)) {
            scheduleResumeRetry();
            return;
        }
        void attemptResume();
    }

    function applyCurrentMix(): void {
        ensureAudioContextRunning();
        if (!gainNode || !pannerNode || !currentSpaceUser) return;
        const resolved = computeSpatialMix(currentSpaceUser, currentBaseVolume, lastSourcePosition);
        const { mix } = resolved;
        lastSourcePosition = resolved.sourcePosition;
        gainNode.gain.setTargetAtTime(mix.gain, audioContext?.currentTime ?? 0, 0.035);
        pannerNode.pan.setTargetAtTime(mix.pan, audioContext?.currentTime ?? 0, 0.035);
    }

    function ensureGraph(stream: MediaStream): boolean {
        if (currentStream === stream && audioContext && sourceNode && gainNode && pannerNode) {
            return true;
        }

        try {
            sourceNode?.disconnect();
            gainNode?.disconnect();
            pannerNode?.disconnect();
            audioContext = audioContextManager.getContext();
            sourceNode = audioContext.createMediaStreamSource(stream);
            gainNode = audioContext.createGain();
            pannerNode = audioContext.createStereoPanner();
            sourceNode.connect(gainNode).connect(pannerNode).connect(audioContext.destination);
            currentStream = stream;
            return true;
        } catch (error) {
            console.warn("Spatial audio graph could not be created.", error);
            return false;
        }
    }

    function enable(stream: MediaStream, spaceUser: SpaceUserExtended, baseVolume: number): void {
        if (!ensureGraph(stream)) return;

        if (currentSpaceUser?.spaceUserId !== spaceUser.spaceUserId || currentSpaceUser.uuid !== spaceUser.uuid) {
            lastSourcePosition = undefined;
        }
        currentSpaceUser = spaceUser;
        currentBaseVolume = baseVolume;
        ensureAudioContextRunning();
        applyCurrentMix();

        if (!interval) {
            interval = setInterval(applyCurrentMix, UPDATE_INTERVAL_MS);
        }
    }

    function disable(baseVolume: number): void {
        currentSpaceUser = undefined;
        currentBaseVolume = baseVolume;
        lastSourcePosition = undefined;
        if (interval) {
            clearInterval(interval);
            interval = undefined;
        }
        sourceNode?.disconnect();
        gainNode?.disconnect();
        pannerNode?.disconnect();
        sourceNode = undefined;
        gainNode = undefined;
        pannerNode = undefined;
        currentStream = undefined;
        clearResumeRetry();
    }

    function destroy(): void {
        disable(currentBaseVolume);
        audioContext = undefined;
    }

    return { enable, disable, destroy };
}

class WorldSpatialAudioManager {
    private audioContext: AudioContext | undefined;
    private interval: ReturnType<typeof setInterval> | undefined;
    private retryUnregister: Unsubscriber | undefined;
    private readonly sources = new Map<string, WorldSpatialAudioSource>();

    isSpatialAudioAreaProperty(property: AreaDataProperty): property is SpatialAudioExtensionProperty {
        return property.type === "extensionModule" && property.subtype === SPATIAL_AUDIO_EXTENSION_SUBTYPE;
    }

    registerAreas(areas: Map<string, AreaData> | undefined, mapUrl: string): void {
        if (!ENABLE_SPATIAL_AUDIO || !areas) return;
        for (const area of areas.values()) {
            for (const property of area.properties) {
                if (this.isSpatialAudioAreaProperty(property)) {
                    this.registerArea(area, property, mapUrl);
                }
            }
        }
    }

    registerArea(area: AreaData, property: SpatialAudioExtensionProperty, mapUrl: string): void {
        if (!ENABLE_SPATIAL_AUDIO) return;
        const config = this.parseAreaConfig(area, property, mapUrl);
        if (!config) return;
        this.unregister(config.id);

        try {
            const audioContext = this.getAudioContext();
            const audioElement = new Audio();
            audioElement.crossOrigin = "anonymous";
            audioElement.src = config.url;
            audioElement.loop = config.loop;
            audioElement.preload = "auto";
            audioElement.volume = 1;

            const sourceNode = audioContext.createMediaElementSource(audioElement);
            const gainNode = audioContext.createGain();
            const pannerNode = audioContext.createStereoPanner();
            sourceNode.connect(gainNode).connect(pannerNode).connect(audioContext.destination);

            const source: WorldSpatialAudioSource = {
                ...config,
                audioElement,
                sourceNode,
                gainNode,
                pannerNode,
                blocked: false,
            };
            this.sources.set(config.id, source);
            this.applySourceMix(source);
            this.startInterval();
            void this.tryPlay(source);
        } catch (error) {
            console.warn("Spatial audio source could not be created.", error);
        }
    }

    unregister(id: string): void {
        const source = this.sources.get(id);
        if (!source) return;
        this.destroySource(source);
        this.sources.delete(id);
        this.stopIntervalIfIdle();
        this.clearRetryIfUnblocked();
    }

    clear(): void {
        for (const source of this.sources.values()) {
            this.destroySource(source);
        }
        this.sources.clear();
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
        this.retryUnregister?.();
        this.retryUnregister = undefined;
        this.audioContext = undefined;
    }

    private getAudioContext(): AudioContext {
        if (!this.audioContext || this.audioContext.state === "closed") {
            this.audioContext = audioContextManager.getContext();
        }
        return this.audioContext;
    }

    private parseAreaConfig(area: AreaData, property: SpatialAudioExtensionProperty, mapUrl: string): WorldSpatialAudioConfig | undefined {
        if (!isRecord(property.data) || property.data.kind !== "spatialAudio") return undefined;
        const audioLink = property.data.audioLink;
        if (typeof audioLink !== "string" || audioLink.trim().length === 0) return undefined;

        const mode = property.data.mode === "point" ? "point" : "zone";
        const volume = clamp(optionalNumber(property.data.volume, 1), 0, 1);
        const loop = optionalBoolean(property.data.loop, true);
        const nearRadius = Math.max(0, optionalNumber(property.data.nearRadius, mode === "zone" ? 0 : SPATIAL_AUDIO_NEAR_RADIUS));
        const farRadius = Math.max(nearRadius + 1, optionalNumber(property.data.farRadius, SPATIAL_AUDIO_FAR_RADIUS));
        const minGain = clamp(optionalNumber(property.data.minGain, SPATIAL_AUDIO_MIN_GAIN), 0, 1);

        const shape: SpatialShape =
            mode === "point"
                ? {
                      mode,
                      x: optionalNumber(property.data.x, area.x + area.width / 2),
                      y: optionalNumber(property.data.y, area.y + area.height / 2),
                  }
                : {
                      mode,
                      x: area.x,
                      y: area.y,
                      width: area.width,
                      height: area.height,
                  };

        return {
            id: property.id,
            url: resolveAudioUrl(audioLink, mapUrl),
            volume,
            loop,
            nearRadius,
            farRadius,
            minGain,
            shape,
        };
    }

    private startInterval(): void {
        if (!this.interval) {
            this.interval = setInterval(() => this.applyAllMixes(), UPDATE_INTERVAL_MS);
        }
    }

    private stopIntervalIfIdle(): void {
        if (this.sources.size === 0 && this.interval) {
            clearInterval(this.interval);
            this.interval = undefined;
        }
    }

    private applyAllMixes(): void {
        for (const source of this.sources.values()) {
            this.applySourceMix(source);
        }
    }

    private applySourceMix(source: WorldSpatialAudioSource): void {
        try {
            const listener = gameManager.getCurrentGameScene().CurrentPlayer;
            let mix: SpatialMix;
            if (source.shape.mode === "zone") {
                const closest = closestPointOnRect(listener.x, listener.y, source.shape);
                mix =
                    closest.distance === 0
                        ? { gain: source.volume, pan: 0 }
                        : computeMixFromPoint(
                              listener,
                              closest.x,
                              closest.y,
                              source.volume,
                              source.nearRadius,
                              source.farRadius,
                              source.minGain,
                          );
            } else {
                mix = computeMixFromPoint(
                    listener,
                    source.shape.x,
                    source.shape.y,
                    source.volume,
                    source.nearRadius,
                    source.farRadius,
                    source.minGain,
                );
            }
            source.gainNode.gain.setTargetAtTime(mix.gain, this.audioContext?.currentTime ?? 0, 0.05);
            source.pannerNode.pan.setTargetAtTime(mix.pan, this.audioContext?.currentTime ?? 0, 0.05);
        } catch {
            source.gainNode.gain.value = 0;
            source.pannerNode.pan.value = 0;
        }
    }

    private async tryPlay(source: WorldSpatialAudioSource): Promise<void> {
        try {
            await this.audioContext?.resume();
            await source.audioElement.play();
            source.blocked = false;
            this.clearRetryIfUnblocked();
        } catch (error) {
            if (error instanceof DOMException && error.name === "NotAllowedError") {
                source.blocked = true;
                signalAudioPlaybackBlocked();
                this.ensureRetry();
                return;
            }
            console.warn("Spatial audio source could not start.", error);
        }
    }

    private ensureRetry(): void {
        if (this.retryUnregister) return;
        this.retryUnregister = audioPlaybackStore.register(async () => {
            const blockedSources = Array.from(this.sources.values()).filter((source) => source.blocked);
            await Promise.all(blockedSources.map((source) => this.tryPlay(source)));
        });
    }

    private clearRetryIfUnblocked(): void {
        if (Array.from(this.sources.values()).some((source) => source.blocked)) return;
        this.retryUnregister?.();
        this.retryUnregister = undefined;
    }

    private destroySource(source: WorldSpatialAudioSource): void {
        try {
            source.audioElement.pause();
            source.audioElement.removeAttribute("src");
            source.audioElement.load();
        } catch {
            // No-op: the source is being torn down anyway.
        }
        source.sourceNode.disconnect();
        source.gainNode.disconnect();
        source.pannerNode.disconnect();
    }
}

export const worldSpatialAudioManager = new WorldSpatialAudioManager();
